import React, { useMemo, useState } from "react";

export default function AddNewCardModal({ user, onClose, onCreated }) {
  const [form, setForm] = useState({
    cardName: "",
    cardCost: "",
    cardPower: "",
    cardDesc: "",
    cardHTML: "",
    customHTML: "", // required for snapRankings inserts
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const apiBase = useMemo(() => process.env.REACT_APP_API_URL ?? "", []);

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate() {
    if (!user?.id) return "Missing user info.";
    if (!form.cardName.trim()) return "Card Name is required.";
    if (String(form.cardName).length > 50) return "Card Name max length is 50.";
    if (form.cardDesc.trim().length === 0) return "Card Description is required.";
    if (String(form.cardDesc).length > 200) return "Description max length is 200.";
    if (!form.cardHTML.trim()) return "Card HTML (image URL/path) is required.";
    if (String(form.cardHTML).length > 200) return "Card HTML max length is 200.";

    const cost = Number(form.cardCost);
    const power = Number(form.cardPower);
    if (!Number.isInteger(cost) || cost < 0) return "Card Cost must be a non-negative integer.";
    if (!Number.isInteger(power)) return "Card Power must be an integer.";

    if (!form.customHTML.trim()) return "customHTML is required (for snapRankings).";
    if (String(form.customHTML).length > 200) return "customHTML max length is 200.";

    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        cardName: form.cardName.trim(),
        cardCost: Number(form.cardCost),
        cardPower: Number(form.cardPower),
        cardDesc: form.cardDesc.trim(),
        cardHTML: form.cardHTML.trim(),
        cardUser: user.id, // snapCards.cardUser
        customHTML: form.customHTML.trim(), // required for rankings inserts
      };

      const base = (apiBase || "").replace(/\/$/, ""); 
      const res = await fetch(`${base}/api/addSnapCardWithRankings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed (${res.status})`);
      }

      const created = await res.json(); // should return the created card (or at least its id)
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create card.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          background: "#111",
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
          padding: 18,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <h2 className="results-heading" style={{ margin: 0 }}>
            Add New Card
          </h2>
          <button className="modern-btn" onClick={onClose} type="button">
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ margin: 20, display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ opacity: 0.9 }}>Card Name</span>
              <input
                value={form.cardName}
                onChange={(e) => update("cardName", e.target.value)}
                maxLength={50}
                style={inputStyle}
                placeholder="e.g., Iron Lad"
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ opacity: 0.9 }}>Card HTML (image URL/path)</span>
              <input
                value={form.cardHTML}
                onChange={(e) => update("cardHTML", e.target.value)}
                maxLength={200}
                style={inputStyle}
                placeholder="https://... or /assets/..."
              />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ opacity: 0.9 }}>Cost</span>
              <input
                value={form.cardCost}
                onChange={(e) => update("cardCost", e.target.value)}
                style={inputStyle}
                inputMode="numeric"
                placeholder="0"
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ opacity: 0.9 }}>Power</span>
              <input
                value={form.cardPower}
                onChange={(e) => update("cardPower", e.target.value)}
                style={inputStyle}
                inputMode="numeric"
                placeholder="0"
              />
            </label>
          </div>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ opacity: 0.9 }}>Description</span>
            <textarea
              value={form.cardDesc}
              onChange={(e) => update("cardDesc", e.target.value)}
              maxLength={200}
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
              placeholder="Card text/description..."
            />
          </label>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ opacity: 0.9 }}>
              customHTML (required) <span style={{ opacity: 0.7 }}>(used for 4 ranking inserts)</span>
            </span>
            <input
              value={form.customHTML}
              onChange={(e) => update("customHTML", e.target.value)}
              maxLength={200}
              style={inputStyle}
              placeholder="e.g., https://... or some custom HTML path"
            />
          </label>

          {error && (
            <div
              style={{
                padding: 10,
                borderRadius: 10,
                background: "rgba(255,0,0,0.12)",
                border: "1px solid rgba(255,0,0,0.25)",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="modern-btn" type="button" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button className="modern-btn" type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Card"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.14)",
  background: "rgba(255,255,255,0.06)",
  color: "white",
  outline: "none",
};
